import request from 'utils/request_ajax';
import { routes } from 'constants/config';
import { initFilestackClient } from './initFilestackClient';

/**
 * Persist a reference to the uploaded Filestack file in the database
 *
 * @param {number} documentId The id of the document record in the database
 * @param {object} upload The Filestack upload record
 * @return {Promise<object>} A Promise that resolves with the server response
 * after saving the document information
 */
function recordUpload(documentId, upload) {
  return request.put(routes.DOCUMENTS_RECORD_UPLOAD(documentId), {
    document: {
      upload_file_name: upload.filename,
      upload_content_type: upload.mimetype,
      upload_file_size: upload.size,
      upload_key: upload.key,
      upload_source: upload.source,
      upload_container: upload.container,
      filestack_handle: upload.handle,
    },
  });
}

/**
 * Uploads a file to Filestack using the picker UI
 *
 * @param {number} documentId The id of the document record in the database
 * @return {Promise<object>} A Promise that resolves with the filestack record
 * of the document
 */
async function uploadDocumentToFilestack(documentId) {
  const { client, uploadOptions, storeOptions } = await initFilestackClient(
    () => request.get(routes.DOCUMENTS_UPLOAD_OPTIONS(documentId)),
  );

  return new Promise((resolve, reject) => {
    const picker = client.picker({
      customText: {
        // Overriding the following text to omit the "Copy and Paste " part from
        // the text because currently copy-paste doesn't work with documents
        // for some reason (note that the old filestack did not mention anything
        // about copy-paste functionality).
        //
        // Note: I'm not using a translation here because the picker is not
        // translated, and the default language is English. Translating the
        // entire picker UI can be done at a later time.
        'or Drag and Drop, Copy and Paste Files': 'or Drag and Drop Files',
      },
      maxFiles: 1,
      accept: uploadOptions.extensions,
      fromSources: uploadOptions.services,
      startUploadingWhenMaxFilesReached: true,
      onUploadDone: response => resolve(response.filesUploaded[0]),
      onFileUploadFailed: (file, error) => reject(error),
      storeTo: storeOptions,
    });

    picker.open();
  });
}

/**
 * Uploads a document to Filestack using the upload picker and persist a
 * reference to the uploaded file to the database for the given document id
 *
 * @param {number} documentId The id of the document record in the database
 * @return {Promise<string>} A Promise that resolves with the preview URL of the
 * the document uploaded
 */
export async function uploadAndRecordDocument(documentId) {
  const filestackUpload = await uploadDocumentToFilestack(documentId);
  const response = await recordUpload(documentId, filestackUpload);
  return response.url;
}
